<style src="./Layer.scss" lang="scss"></style>
<template src="./Layer.html"></template>

<script>
	import Ad from '@/components/Ad/Ad';
	import List from '@/components/List/List';
	import Teaser from '@/components/Teaser/Teaser';
	import Button from '@/components/Button/Button';
	import Icon from '@/components/Icon/Icon';
	import Searchbar from '@/components/Searchbar/Searchbar';
	import { AdServiceWrapper } from '@/services/wrapper/adServiceWrapper';
	import { Helper } from '@/services/helper/helper';

	/**
	 * The Layer component for the page.
	 *
	 * Currently only used for the inactivity layer.
	 * The Layer can be enforced by setting the GET param _inactivityLayerDuration=X in the url, while X are the ms for the delay
	 *
	 * @vue-prop {Object} meta The meta data, e.g. for tracking
	 * @vue-prop {Object[]} elements The elements like teasers and ads
	 * @vue-prop {String} headline The main headline
	 * @vue-prop {String} teasersHeadline The headline above the teasers
	 * @vue-prop {String} hideAlwaysText The text used for the hide always link
	 * @vue-prop {Object} button The data required for the button { importance, icon, text }
	 * @vue-prop {Number} delay The initial delay after which time period the layer should be displayed
	 * @vue-data {Object[]} teasers The teasers
	 * @vue-data {Object} ad The ad for the right side
	 * @vue-data {Number} delayTime Internal time of the delay
	 * @vue-data {Boolean} hiddenPermanent Determines whether the layer is permanently hidden or not
	 * @vue-data {Number} minColCount The minimum amount of cols on the page to show the layer
	 * @vue-data {Object} timer Internal pointer to the timeout to show the layer
	 * @vue-data {Boolean} timerStopped true if the timer is stopped and needs to be reactivated
	 * @vue-data {Object} urlParams The url parameters
	 * @vue-data {Boolean} visible Determines whether the layer is visible or not
	 * @vue-computed {Object} normalizedButton The button data merged with default values { importance, icon, text }
	 * @listens Document#colCountChanged
	 * @listens Window#scroll
	 */
	export default {
		props: {
			meta: Object,
			elements: Array,
			headline: String,
			teasersHeadline: String,
			hideAlwaysText: String,
			button: Object,
			delay: {
				type: Number,
				default: 300000
			}
		},
		data () {
			return {
				teasers: this.elements?.filter((e) => ['hp-teaser', 'hp-list'].includes(e.type)),
				ad: this.elements?.filter((e) => e.type === 'hp-ad')?.[0],
				visible: false,
				hiddenPermanent: false,
				delayTime: this.delay,
				timer: null,
				timerStopped: false,
				minColCount: 4,
				urlParams: new URLSearchParams(window.location.search)
			};
		},
		computed: {
			normalizedButton () {
				return {
					importance: process.env.VUE_APP_BRAND === 'webde' ? 'primary' : 'secondary',
					icon: 'refresh',
					...this.button
				};
			}
		},
		watch: {
			visible () {
				this.visible ? this.openLayer() : this.closeLayer();
			}
		},
		methods: {
			openLayer () {
				if (this.hiddenPermanent || this.timerStopped) {
					return;
				}

				document.body.setAttribute('data-inactivity-layer', '');

				if (this.$el.querySelector('[data-service-slot]')) {
					this.$el.querySelector('[data-service-slot]').innerHTML = '';
					setTimeout(() => {
						AdServiceWrapper.makeAd('rectangle_layer');
					}, 50); // short timeout because otherwise the ad doesn't get loaded
				}

				setTimeout(() => {
					Helper.fireEvent('layerOpened');
				}, 50);
			},
			closeLayer () {
				document.body.removeAttribute('data-inactivity-layer');

				Helper.fireEvent('layerClosed');

				if (!this.hiddenPermanent) {
					this.startTimer();
				}
			},
			hidePermanent () {
				localStorage.setItem('info-layer-home-inactive', '1');
				this.hiddenPermanent = true;
				this.visible = false;
			},
			reloadSite () {
				this.visible = false;
				window.location.reload(false);
			},
			startTimer () {
				window.onload = this.resetTimer;
				window.onmousemove = this.resetTimer;
				window.onmousedown = this.resetTimer;
				window.ontouchstart = this.resetTimer;
				window.onclick = this.resetTimer;
				window.onkeypress = this.resetTimer;
				window.addEventListener('scroll', this.resetTimer, true);
			},
			checkLayerVisibility () {
				let colCount = document.body.getAttribute('data-col-count');
				if (colCount < this.minColCount) {
					this.visible = false;
					clearTimeout(this.timer);
				} else {
					this.resetTimer();
				}
			},
			resetTimer () {
				clearTimeout(this.timer);

				if (this.hiddenPermanent || this.timerStopped) {
					return;
				}

        const _this = this;
        this.timer = setTimeout(() => {
          let colCount = document.body.getAttribute('data-col-count');
          if (colCount < _this.minColCount) {
            _this.visible = false;
            clearTimeout(_this.timer);
          } else {
            _this.visible = true;
          }
        }, this.delayTime);
			},
			stopTimer () {
				this.timerStopped = true;
				this.visible = false;
				clearTimeout(this.timer);
			},
			restartTimer () {
				this.timerStopped = false;
				this.resetTimer();
			}
		},
		mounted () {
			let layerOverwriteTime = +this.urlParams?.get('_inactivityLayerDuration');

			if (layerOverwriteTime > 0) {
				this.delayTime = layerOverwriteTime;
			} else if (localStorage.getItem('info-layer-home-inactive') === '1') {
				this.hiddenPermanent = true;
				return;
			}

			this.startTimer();
			document.addEventListener('colCountChanged', this.checkLayerVisibility);
			document.addEventListener('pppLayerVisible', this.stopTimer);
			document.addEventListener('pppLayerClosed', this.restartTimer);
		},
		components: {
			'hp-ad': Ad,
			'hp-list': List,
			'hp-teaser': Teaser,
			'hp-button': Button,
			'hp-icon': Icon,
			'hp-searchbar': Searchbar
		}
	};
</script>
